import { IonInfiniteScroll, useIonViewWillLeave } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'

import { useGetUnitTrafficInfinite } from '@apps-common/apis/portal/unit'
import { EventItemCard, EventItemCardSkeleton } from '@apps-common/apps/portal/pages/units/pages/manage/pages/events/components'
import { CardContainer, InfiniteLoader, IntersectCallbackCard, NoResultsCard, PullRefresh } from '@apps-common/components/common'
import { LayoutPadding, PageContentContainerInline } from '@apps-common/components/layout'
import { SignalStrengthChart, SignalStrengthKpi } from '@apps-common/components/modals/signal-strength-modal'
import { EndOfResults } from '@apps-common/components/modals/unit-history'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn, motionSlideDownFadeIn } from '@apps-common/config/motion-variants'
import { useSignalR } from '@apps-common/contexts/SignalrContext'
import { useWindowDimensions } from '@apps-common/hooks/useWindowDimensions'
import { IUnitGenericDetailsModel, IUnitTrafficModel, IUnitTrafficStreamModel } from '@apps-common/types/portal/unit'
import { HubConnectionState } from '@microsoft/signalr'

const TAKE = 15

interface IProps {
  isAtTop: boolean
  unit?: IUnitGenericDetailsModel
}

export const UnitEventsHistory: React.FC<IProps> = ({ isAtTop, unit }) => {
  const { connection, status } = useSignalR()
  const { ipAddress, imei } = unit || {}
  const { isMobile, isTablet } = useWindowDimensions()
  const [timestamp, setTimestamp] = useState<string>(moment().toISOString())
  const [indexInView, setIndexInView] = useState<number>(0)
  const [interactionItem, setInteractionItem] = useState<IUnitTrafficModel>()
  const [currentSlice, setCurrentSlice] = useState<IUnitTrafficModel[]>([])
  const [currentSliceRange, setCurrentSliceRange] = useState<string>()
  const [messages, setMessages] = useState<IUnitTrafficStreamModel[]>([])
  const [messagesCountViewed, setMessagesCountViewed] = useState<number>(0)

  const messagesRef = useRef(messages)

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const { isLoading, isRefetching, isFetching, isError, data, refetch, fetchNextPage, hasNextPage } = useGetUnitTrafficInfinite({
    config: {
      enabled: !!ipAddress && !!imei
    },
    identifiers: `${ipAddress},${imei}`,
    startDate: timestamp,
    take: TAKE
  })

  useIonViewWillLeave(() => {
    // *** This is where you should dispose of resources or reset context-specific state ***
    console.log('Page is leaving, running cleanup...')
    // Example: Resetting a specific part of the context state related to this page
    connection?.stop()
    // Example: Unsubscribe from an observable (if you have one)
    // mySubscription.unsubscribe();
  })

  useEffect(() => {
    if (connection && status === HubConnectionState.Connected) {
      console.log('Connection State... Subscribing to RecieveMessage')

      const handleMessage = (message: any) => {
        console.log('RecieveMessage event received:', message)
        // if (!isRefetching && !isLoading) {
        setMessages((prevMessages) => [...prevMessages, message])
        if (isAtTop) {
          setMessagesCountViewed((prevCount) => prevCount + 1)
        }
        // }
      }

      // Register the event listener
      connection.on('RecieveMessage', handleMessage)

      // Cleanup the event listener on unmount or when `connection` changes
      return () => {
        connection.off('RecieveMessage', handleMessage)
      }
    }
  }, [connection])

  const getCurrentDataSlice = () => {
    if (data?.pages) {
      let length = data?.pages?.length

      if (length < 1) {
        length = 1
      }

      const slice = data?.pages[indexInView]

      if (indexInView === 0) {
        return [...(messages as any[]), ...slice]
      }

      return slice
    }

    return undefined
  }

  useEffect(() => {
    if (data) {
      const slice = getCurrentDataSlice() || []

      if (slice.length > 0) {
        const range = `${moment(slice[slice.length - 1].timeStamp).format('Do MMM')} to ${moment(slice[0].timeStamp).format('Do MMM')}`

        setCurrentSlice(slice)
        setCurrentSliceRange(range)
      }
    }
  }, [messages, data, indexInView])

  useEffect(() => {
    if (isRefetching || isLoading) {
      setMessages([])
    }
  }, [isRefetching, isLoading])

  // const hasData = data && data.pages && data.pages.length > 0
  const loading = isLoading || isError

  console.log('Events loading: ', loading, data)
  const getEventKey = (prefix: string, event: Partial<IUnitTrafficModel>, index: number, pageIndex?: number) => {
    const id = event?.id ?? 'no-id'
    const timeStamp = event?.timeStamp ?? 'no-ts'
    return `${prefix}-${pageIndex ?? 'p'}-${id}-${timeStamp}-${index}`
  }

  return (
    <LayoutPadding classes='pt-[20px] sm:pt-[20px]'>
      <PullRefresh loading={isRefetching} callback={refetch} />
      <div id='events-top-marker' />
      <div className='flex flex-col w-full md:max-w-[360px] lg:max-w-[460px] xl:max-w-[510px]'>
        <AnimatePresence>
          <PageContentContainerInline
            key='unit-events-new-events-banner'
            useMargin
            classes='w-full md:w-[360px] lg:w-[460px] xl:w-[510px] top-[180px] sm:top-[210px] h-[30px] fixed z-[5] text-center flex items-center justify-center'
          >
            {messages.length > messagesCountViewed && !isAtTop && (
              <motion.button
                key='new-event-messages-scroll-up'
                {...motionSlideDownFadeIn(0.35)}
                onClick={() => {
                  const ele = document.getElementById('events-top-marker')

                  if (ele) {
                    ele.scrollIntoView({ block: 'end', behavior: 'smooth' })
                  }

                  setMessagesCountViewed(messages.length)
                }}
                className='bg-secondary-content text-secondary px-[10px] py-[5px] rounded-xl shadow-lg flex items-center justify-center gap-[10px] text-[0.9rem] jost-light'
              >
                New Events
                {/* <div className='border-[1px] border-secondary w-[20px] h-[20px] p-[3px] rotate-[-90deg] rounded-full'> */}
                <commonIcons.arrowRight className='text-[1rem] rotate-[-90deg]' />
                {/* </div> */}
              </motion.button>
            )}
          </PageContentContainerInline>
          <div key='unit-events-live-stream' className='relative flex flex-col gap-[15px]'>
            {messages.map((m, i) => (
              <motion.div
                key={getEventKey(`${unit?.unitId || 'unit'}-traffic-stream`, m as any, i)}
                initial='initial'
                animate='animate'
                variants={{
                  initial: {
                    top: '-20px',
                    height: 0,
                    opacity: 0,
                    transition: {
                      duration: 0.3
                    }
                  },
                  animate: {
                    top: '0px',
                    height: 'auto',
                    opacity: 1,
                    transition: {
                      duration: 0.3
                    }
                  }
                }}
              >
                <EventItemCard event={m as any} interactionEvent={interactionItem} />
              </motion.div>
            ))}
            {messages?.length > 0 && (
              <motion.div
                key={`traffic-live-events-end-separator`}
                initial='initial'
                animate='animate'
                variants={{
                  initial: {
                    opacity: 0,
                    transition: {
                      duration: 0.3
                    }
                  },
                  animate: {
                    opacity: 1,
                    transition: {
                      duration: 0.3
                    }
                  }
                }}
                className='flex items-center justify-center mt-[20px] mb-[35px] gap-[20px]'
              >
                <div className='grow h-[1px] bg-primary-content/30' />
                <div className='text-primary-content/40 jost-light text-[0.75rem] uppercase'>End of new events</div>
                <div className='grow h-[1px] bg-primary-content/30' />
              </motion.div>
            )}
          </div>
          <div key='unit-events-history' className='relative flex flex-col gap-[15px]'>
            {loading &&
              Array.from(Array(TAKE).keys()).map((e, i) => (
                <motion.div key={`traffic-history-event-item-skeleton-${i}`} {...motionFadeIn(0.25)}>
                  <EventItemCardSkeleton />
                </motion.div>
              ))}
            {!loading && currentSlice?.length === 0 && (
              <motion.div key={'traffic-history-no-events'} {...motionFadeIn(0.25)}>
                <NoResultsCard>This device has no events</NoResultsCard>
              </motion.div>
            )}
            {!loading &&
              data?.pages?.map((p, i) => (
                <motion.div key={`event-group-${i}`} {...motionFadeIn(0.25)} className='relative'>
                  <IntersectCallbackCard id={i} padding='-30px' callback={(index) => setIndexInView(index)} />
                  <div className='flex flex-col gap-[15px]'>
                    {p.map((e, eventIndex) => (
                      <EventItemCard
                        key={getEventKey('traffic-history-event-item', e, eventIndex, i)}
                        event={e}
                        interactionEvent={interactionItem}
                        containerClasses='!bg-surface'
                        innerClasses='!bg-surface-2'
                      />
                    ))}
                  </div>
                </motion.div>
              ))}
            <EndOfResults show={!loading && !hasNextPage && currentSlice?.length !== 0} />
          </div>
        </AnimatePresence>
        <IonInfiniteScroll
          onIonInfinite={async (ev) => {
            if (hasNextPage) {
              await fetchNextPage()
            }
            ev.target.complete()
          }}
        >
          <InfiniteLoader key='unit-events-history-list-infinite-loader' loading={isFetching} />
        </IonInfiniteScroll>
      </div>
      {!isMobile && !isTablet && (
        <div className='md:ml-[410px] lg:ml-[510px] xl:ml-[560px] top-[190px] fixed'>
          <CardContainer bgClasses='bg-surface-2 border border-border max-w-[350px] md:max-w-[380px] lg:max-w-[480px] xl:max-w-[480px] lg:!p-[30px]'>
            <div className='flex items-start justify-between mb-[10px]'>
              <div className='text-[0.7em] mt-[5px] leading-[0.7em] jost items-center justify-start bg-surface text-text rounded-2xl px-[7px] py-[5px] w-fit'>
                Signal Strength
              </div>
              <SignalStrengthKpi loading={loading} noIcon data={currentSlice} />
            </div>
            <div className='jost-light text-[0.9rem] text-secondary-content text-center mb-[10px]'>Signal strength by event</div>

            <div className='relative'>
              {!loading && currentSlice?.length === 0 && (
                <div className='absolute z-[5] top-0 left-0 w-full h-full bg-secondary/5 backdrop-blur-sm flex items-center justify-center'>
                  <div className='bg-secondary-content/5 px-4 py-3 rounded-xl jost-light'>No signals</div>
                </div>
              )}
              <SignalStrengthChart
                loading={loading}
                classes='w-full sm:max-h-[260px] lg:!max-h-[300px] bg-surface p-3 rounded-3xl'
                data={getCurrentDataSlice()}
                pointInteractionCallback={setInteractionItem}
              />
            </div>

            <div className='flex flex-col items-center justify-center pt-[10px] gap-[10px]'>
              <div className='bg-surface px-4 py-2 rounded-full overflow-hidden min-w-[200px] text-center'>
                <AnimatePresence mode='popLayout'>
                  <motion.div
                    key={currentSliceRange}
                    initial='initial'
                    animate='animate'
                    exit='exit'
                    variants={{
                      initial: {
                        bottom: '-40px',
                        opacity: 0,
                        transition: {
                          duration: 0.25,
                          ease: 'easeInOut'
                        }
                      },
                      animate: {
                        top: '0px',
                        opacity: 1,
                        transition: {
                          duration: 0.25,
                          ease: 'easeInOut'
                        }
                      },
                      exit: {
                        top: '-40px',
                        opacity: 0,
                        transition: {
                          duration: 0.25,
                          ease: 'easeInOut'
                        }
                      }
                    }}
                    className='relative jost-light text-[0.9em]'
                  >
                    {currentSliceRange || <commonIcons.dash className='mx-auto' />}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </CardContainer>
        </div>
      )}
    </LayoutPadding>
  )
}
